import { useState, useEffect, useCallback } from 'react';
import FingerprintJS, { GetResult } from '@fingerprintjs/fingerprintjs';
import { supabase } from '@/integrations/supabase/client';

interface FraudValidation {
    valid: boolean;
    risk_score: number;
    issues: Array<{
        type: string;
        message: string;
        blocking: boolean;
    }>;
    require_review: boolean;
}

interface UseFingerprint {
    fingerprint: string | null;
    loading: boolean;
    error: string | null;
    validateEmail: (email: string) => Promise<FraudValidation>;
    recordDevice: (userId: string) => Promise<void>;
}

export function useFingerprint(): UseFingerprint {
    const [fingerprint, setFingerprint] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const loadFingerprint = async () => {
            try {
                const fp = await FingerprintJS.load();
                const result: GetResult = await fp.get();
                setFingerprint(result.visitorId);
            } catch (err) {
                console.warn('[useFingerprint] Failed to load fingerprint:', err);
                // Don't block on fingerprint errors
                setError('Fingerprint unavailable');
            } finally {
                setLoading(false);
            }
        };

        loadFingerprint();
    }, []);

    const validateEmail = useCallback(async (email: string): Promise<FraudValidation> => {
        try {
            const { data, error: fnError } = await supabase.functions.invoke('fraud-detection', {
                body: {
                    action: 'validate_signup',
                    email,
                    fingerprint,
                },
            });

            if (fnError) {
                console.warn('[validateEmail] Function error:', fnError);
                // Don't block on validation errors
                return { valid: true, risk_score: 0, issues: [], require_review: false };
            }

            return data as FraudValidation;
        } catch (err) {
            console.warn('[validateEmail] Error:', err);
            return { valid: true, risk_score: 0, issues: [], require_review: false };
        }
    }, [fingerprint]);

    const recordDevice = useCallback(async (userId: string): Promise<void> => {
        if (!fingerprint) {
            console.warn('[recordDevice] No fingerprint available');
            return;
        }

        try {
            const { error: fnError } = await supabase.functions.invoke('fraud-detection', {
                body: {
                    action: 'record_device',
                    userId,
                    fingerprint,
                },
            });

            if (fnError) {
                console.warn('[recordDevice] Function error:', fnError);
            }
        } catch (err) {
            console.warn('[recordDevice] Error:', err);
        }
    }, [fingerprint]);

    return {
        fingerprint,
        loading,
        error,
        validateEmail,
        recordDevice,
    };
}
